/* eslint-disable max-lines */
import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  inject,
} from '@angular/core';

import { select, Store } from '@ngrx/store';
import { fromEvent as observableFromEvent, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import {
  NotificationsAnalytics,
  NotificationTarget,
  TagsAnalytics,
  TagsTarget,
} from '@core/analytics-legacy';
import { AnalyticsMode } from '@core/analytics-types';
import {
  CandidateAnalytics,
  ShareCandidatesWithContextAnalytics,
  TrackProceedOrigin,
} from '@core/candidate-util';
import { GdprAnalyticsService } from '@core/gdpr-data';
import { GdprService } from '@core/gdpr-logic';
import { TranslatePipe } from '@core/i18n-legacy';
import type { TOfferStatus } from '@core/offers-types';
import { SpamVerificationService } from '@core/spam-verification-logic';
import { TasksAnalytics } from '@core/tasks-util';
import {
  BadgeComponent,
  ButtonComponent,
  ButtonDropdownIndicatorDirective,
  ButtonLoadingDirective,
  CheckboxComponent,
  ConnectedOverlayDirective,
  DialogService,
  DropdownComponent,
  DropdownItemComponent,
  DropdownWidth,
  GroupComponent,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
  RtButtonVariant,
  ScrollableDirective,
  TruncateComponent,
  WindowRef,
} from '@core/user-interface-legacy';

import { BulkActionsBatchSelectComponent } from '../bulk-actions-batch-select/bulk-actions-batch-select.component';
import { BulkActionsController } from '../bulk-actions.controller';
import { BulkActionsSelectionTracker } from '../bulk-actions.selection-tracker';
import type { BulkAction, BulkItemBase } from '../bulk-actions.types';
import { BulkActionType } from '../bulk-actions.types';
import type { TaskFormChangeEvent } from '../types';

export interface ActionsVisibilityBreakpoint {
  min: number;
  max: number;
  actionIds: BulkActionType[];
}

export type BulkActionsContext<
  OfferScope extends string = string,
  ViewType extends string = string,
  RequisitionsScope extends string = string,
> =
  | {
      offer?: { kind: 'job' | 'talent_pool'; id: number; status: TOfferStatus };
      offersScope?: OfferScope;
      searchViewType?: ViewType;
      requisitionsScope?: RequisitionsScope;
    }
  | undefined;

@Component({
  selector: 'rt-bulk-actions-toolbar',
  templateUrl: './bulk-actions-toolbar.component.html',
  styleUrls: ['./bulk-actions-toolbar.component.less'],
  host: {
    '(document:keydown.escape)': '!dialog.hasDialogsOpen() ? clearSelection() : null',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    GdprAnalyticsService,
    CandidateAnalytics,
    NotificationsAnalytics,
    TagsAnalytics,
    TasksAnalytics,
  ],
  imports: [
    GroupComponent,
    BulkActionsBatchSelectComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    IconComponent,
    TruncateComponent,
    ButtonDropdownIndicatorDirective,
    OverlayOriginDirective,
    ConnectedOverlayDirective,
    DropdownComponent,
    ScrollableDirective,
    DropdownItemComponent,
    CheckboxComponent,
    IconButtonComponent,
    TranslatePipe,
    BadgeComponent,
  ],
})
export class BulkActionsToolbarComponent {
  private readonly bulkCtrl = inject(BulkActionsController);
  private readonly window = inject(WindowRef);
  private readonly elRef = inject(ElementRef);
  private readonly bulkTracker = inject(BulkActionsSelectionTracker);
  private readonly gdprSvc = inject(GdprService);
  private readonly gdprAnalyticsService = inject(GdprAnalyticsService);
  private readonly candidateAnalytics = inject(CandidateAnalytics);
  private readonly notificationsAnalytics = inject(NotificationsAnalytics);
  private readonly tagsAnalytics = inject(TagsAnalytics);
  private readonly tasksAnalytics = inject(TasksAnalytics);
  private readonly store = inject(Store);
  private readonly spamVerificationService = inject(SpamVerificationService);
  private readonly shareCandidatesWithContextAnalytics = inject(
    ShareCandidatesWithContextAnalytics,
    { optional: true },
  );

  protected readonly cdRef = inject(ChangeDetectorRef);
  public readonly dialog = inject(DialogService);

  @Input() public context: BulkActionsContext;
  @Input() public buttonMode: 'batchSelect' | 'toggle' = 'batchSelect';
  @Input() public items: BulkItemBase[];
  @Input() public variant: Extract<RtButtonVariant, 'tertiary' | 'outline'> = 'tertiary';

  public DropdownWidth = DropdownWidth;

  public active = false;
  public selectedItems: BulkItemBase[] = [];
  public visibleActions: BulkAction[] = [];
  public hiddenActions: BulkAction[] = [];
  public dropdownOpen = false;
  public dropdownHeight = 'auto';

  private abilities = {
    deleteCandidates: false,
    manageCandidates: false,
    sendEmails: false,
    shareCandidates: false,
    viewCandidates: false,
    manageJobs: false,
    manageRequisitions: false,
  };
  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit(): void {
    this.bulkCtrl.active$.pipe(takeUntil(this.ngOnDestroy$.asObservable())).subscribe(active => {
      this.dropdownOpen = !active ? false : this.dropdownOpen;
      this.active = active;
      this.cdRef.detectChanges();
    });

    this.bulkCtrl.selectedItems$
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(selectedItems => {
        this.selectedItems = selectedItems;
        this.cdRef.detectChanges();
      });

    this.bulkCtrl.ready$.pipe(takeUntil(this.ngOnDestroy$.asObservable())).subscribe(() => {
      this.recalculateActions();
    });

    observableFromEvent(this.window.nativeWindow, 'resize')
      .pipe(debounceTime(100), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => {
        this.recalculateActions();
      });

    this.bulkCtrl.recalculateActions$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
      this.recalculateActions();
    });

    this.checkAbilities();
  }

  public ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      const offsetTop =
        this.elRef.nativeElement.getBoundingClientRect().top +
        this.elRef.nativeElement.getBoundingClientRect().height +
        150;
      this.dropdownHeight = `calc(100vh - ${offsetTop}px)`;
      this.cdRef.detectChanges();
    });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { context, items } = changes;

    if (items) {
      this.bulkTracker.items = items.currentValue || [];
    }

    if (!context?.firstChange) {
      this.recalculateActions();
    }
  }

  public isActionInProgress(action: BulkAction): boolean {
    return this.bulkCtrl.isActionInProgress(action);
  }

  public trackByAction(index: number, action: BulkAction): BulkActionType {
    return action.id;
  }
  // eslint-disable-next-line max-lines-per-function
  public dispatchAction(action: BulkAction): void {
    if (action.id === BulkActionType.Extend && this.gdprAnalyticsService) {
      action.analytics = () => {
        this.gdprAnalyticsService?.trackExtendRetentionPeriod('bulk', this.selectedItems.length);
      };
    }

    // eslint-disable-next-line default-case
    switch (action.id) {
      case BulkActionType.ChangeStage:
        action.analytics = () =>
          this.candidateAnalytics.trackProceed(
            TrackProceedOrigin.STAGE_SELECT,
            AnalyticsMode.Bulk,
            this.selectedItems.length,
          );
        break;
      case BulkActionType.Disqualify:
        action.analytics = () =>
          this.candidateAnalytics.trackDisqualify(AnalyticsMode.Bulk, this.selectedItems.length);
        break;
      case BulkActionType.Requalify:
        action.analytics = () =>
          this.candidateAnalytics.trackRequalify(AnalyticsMode.Bulk, this.selectedItems.length);
        break;
      case BulkActionType.Delete:
        action.analytics = () =>
          this.candidateAnalytics.trackDelete(AnalyticsMode.Bulk, this.selectedItems.length);
        break;
      case BulkActionType.Follow:
        action.analytics = () =>
          this.notificationsAnalytics.trackFollowAction(
            this.context?.offer?.kind ||
              this.context?.requisitionsScope ||
              NotificationTarget.Candidate,
            AnalyticsMode.Bulk,
            this.selectedItems.length,
          );
        break;
      case BulkActionType.Unfollow:
        action.analytics = () =>
          this.notificationsAnalytics.trackUnfollowAction(
            this.context?.offer?.kind ||
              this.context?.requisitionsScope ||
              NotificationTarget.Candidate,
            AnalyticsMode.Bulk,
            this.selectedItems.length,
          );
        break;
      case BulkActionType.AddTags:
        action.analytics = () =>
          this.tagsAnalytics.trackTagAdded(
            this.context?.offer?.kind || TagsTarget.Candidate,
            AnalyticsMode.Bulk,
            this.selectedItems.length,
          );
        break;
      case BulkActionType.AddSources:
        action.analytics = () =>
          this.tagsAnalytics.trackSourceAdded(AnalyticsMode.Bulk, this.selectedItems.length);
        break;

      case BulkActionType.AddTask:
        action.analyticsWithPayload = ({
          candidatesCount,
          task,
        }: {
          candidatesCount: number;
          task: TaskFormChangeEvent;
        }) =>
          this.tasksAnalytics.trackAddTask(
            AnalyticsMode.Bulk,
            !!task.dueDate,
            !!task.adminIds.length,
            candidatesCount,
          );
        break;

      case BulkActionType.Share:
        action.analytics = () =>
          this.shareCandidatesWithContextAnalytics?.trackClickShare(AnalyticsMode.Bulk);
        break;
    }

    this.bulkCtrl.dispatchAction({ action, offer: this.context?.offer || null });
  }

  public toggleDropdown(open: boolean): void {
    this.dropdownOpen = open;
    this.cdRef.detectChanges();
  }

  public toggleActive(): void {
    this.bulkCtrl.toggleActive();
  }

  public clearSelection(): void {
    this.bulkCtrl.clearSelection();
  }

  public recalculateActions(): void {
    const actions = this.bulkCtrl.getBulkActions(this.context);
    const windowWidth: number = this.window.nativeWindow.innerWidth;
    const breakpointMatch = this.bulkCtrl
      .getActionsVisibilityByBreakpoint(this.context?.searchViewType)
      .find(breakpoint => windowWidth >= breakpoint.min && windowWidth <= breakpoint.max)!;

    if (!breakpointMatch) {
      this.visibleActions = [];
      this.hiddenActions = actions;
    }

    const { visibleActions, hiddenActions } = actions.reduce(
      (acc, action) => {
        if (breakpointMatch.actionIds.includes(action.id)) {
          acc.visibleActions.push(action);
        } else {
          acc.hiddenActions.push(action);
        }
        return acc;
      },
      { visibleActions: <BulkAction[]>[], hiddenActions: <BulkAction[]>[] },
    );

    this.visibleActions = visibleActions.filter(action => this.isActionPossible(action));
    this.hiddenActions = hiddenActions.filter(action => this.isActionPossible(action));
    this.cdRef.detectChanges();
  }

  private isActionPossible(action: BulkAction): boolean {
    if (this.context?.requisitionsScope) {
      switch (action.id) {
        case BulkActionType.Archive:
        case BulkActionType.RetrieveArchived:
          return this.abilities.manageRequisitions;
        default:
          return true;
      }
    } else {
      switch (action.id) {
        case BulkActionType.SendMail:
          return (
            this.abilities.manageCandidates &&
            this.abilities.sendEmails &&
            !this.spamVerificationService.verificationRequired()
          );
        case BulkActionType.Extend:
          return this.gdprSvc.isGdprEnabled(true);
        case BulkActionType.Delete:
          return this.abilities.deleteCandidates;
        case BulkActionType.Follow:
        case BulkActionType.Unfollow:
        case BulkActionType.AddTask:
          return this.abilities.viewCandidates;
        case BulkActionType.Share:
          return this.abilities.shareCandidates;
        default:
          return this.abilities.manageCandidates;
      }
    }
  }

  private checkAbilities(): void {
    this.store
      .pipe(select(AbilitiesSelectors.abilityMap), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(enabledMap => {
        this.abilities = {
          deleteCandidates: enabledMap[Ability.DeleteCandidates],
          manageCandidates: enabledMap[Ability.ManageCandidates],
          manageJobs: enabledMap[Ability.ManageJobs],
          sendEmails: enabledMap[Ability.SendEmails],
          shareCandidates: enabledMap[Ability.ShareCandidates],
          viewCandidates: enabledMap[Ability.ViewCandidates],
          manageRequisitions: enabledMap[Ability.ManageRequisitions],
        };
        this.recalculateActions();
      });
  }
}
